/**
 * Seed pools for the N8N mock generators.
 *
 * Deliberately hand-written (no faker dep) so the mock layer stays a
 * zero-dependency, self-contained module. Pools are biased toward B2B SaaS
 * outreach realism: tech-flavored companies, ICP-flavored copy, channel
 * handles that look like enriched data.
 */

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export const FIRST_NAMES = [
  'Sofia', 'Lars', 'Mei', 'Diego', 'Anya', 'Noah', 'Camila', 'Henrik', 'Priya',
  'Mateo', 'Elena', 'Jonas', 'Yuki', 'Lucas', 'Aïcha', 'Sven', 'Ingrid', 'Rafael',
  'Hannah', 'Tobias', 'Nadia', 'Felix', 'Clara', 'Adrien', 'Leila', 'Markus',
] as const;

export const LAST_NAMES = [
  'Andersen', 'Müller', 'Rossi', 'García', 'Novak', 'Tanaka', 'Berg', 'Dubois',
  'Fernández', 'Lindqvist', 'Costa', 'Schmidt', 'Petrov', 'Olsen', 'Janssen',
  'Marti', 'Kowalski', 'Nielsen', 'Reyes', 'Bianchi', 'Hauser', 'Silva', 'Eriksson',
] as const;

export const ROLES = [
  'CEO', 'Founder', 'CTO', 'VP of Sales', 'Head of Growth', 'VP Marketing',
  'Chief Revenue Officer', 'COO', 'VP of Operations', 'Head of Partnerships',
  'Director of Demand Gen', 'VP of Customer Success', 'Head of Product',
] as const;

export const SENIORITY = ['C-level', 'VP', 'Director', 'Head', 'Senior Manager'] as const;

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export const COMPANY_PREFIXES = [
  'Nexus', 'Fabel', 'Lumen', 'Vertex', 'Orbit', 'Helio', 'Quanta', 'Nimbus',
  'Cobalt', 'Aster', 'Forge', 'Halcyon', 'Meridian', 'Solace', 'Vantage', 'Pulse',
  'Strata', 'Cardinal', 'Apex', 'Lattice', 'Onyx', 'Tessera', 'Northwind', 'Zephyr',
] as const;

export const COMPANY_SUFFIXES = [
  'Labs', 'AI', 'Cloud', 'Systems', 'Analytics', 'Ops', 'Stack', 'Flow', 'HQ',
  'Group', 'Works', 'Suite', 'Hub', 'Forge', 'Technologies',
] as const;

export const INDUSTRIES = [
  'SaaS', 'Fintech', 'Martech', 'Cybersecurity', 'AI / Machine Learning', 'HR Tech',
  'DevTools', 'E-commerce', 'HealthTech', 'Data Infrastructure', 'Climate Tech',
  'Logistics', 'EdTech', 'Legal Tech',
] as const;

export const COUNTRIES = [
  'Germany', 'Spain', 'Sweden', 'Netherlands', 'United Kingdom', 'France',
  'Denmark', 'Norway', 'United States', 'Canada', 'Portugal', 'Ireland',
] as const;

export const COUNTRY_CODES = ['DE', 'ES', 'SE', 'NL', 'GB', 'FR', 'DK', 'NO', 'US', 'CA', 'PT', 'IE'] as const;

export const CITIES = [
  'Berlin', 'Madrid', 'Stockholm', 'Amsterdam', 'London', 'Paris', 'Copenhagen',
  'Oslo', 'New York', 'Toronto', 'Lisbon', 'Dublin',
] as const;

export const TIMEZONES = [
  'Europe/Berlin', 'Europe/Madrid', 'Europe/Stockholm', 'Europe/Amsterdam',
  'Europe/London', 'Europe/Paris', 'Europe/Copenhagen', 'Europe/Oslo',
  'America/New_York', 'America/Toronto', 'Europe/Lisbon', 'Europe/Dublin',
] as const;

export const EMPLOYEE_RANGES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000'] as const;

export const REVENUE_RANGES = ['$0-1M', '$1-5M', '$5-10M', '$10-50M', '$50-100M', '$100M+'] as const;

// ---------------------------------------------------------------------------
// ICP / Outreach copy
// ---------------------------------------------------------------------------

export const PAINS = [
  'Manual lead research eating half the SDR team\'s week',
  'Outreach tools that can\'t personalize at scale',
  'Pipeline forecasting that drifts every quarter',
  'Customer data scattered across five tools',
  'Onboarding takes longer than the trial',
  'Attribution that never ties revenue to the channel',
] as const;

export const TRIGGERS = [
  'Just raised a Series B and scaling the sales team',
  'Posted three SDR roles in the last 30 days',
  'New CRO joined last month — typical ops reset window',
  'Switched CRM vendors in the last two quarters',
  'Expanded into DACH and needs local outbound',
  'Trial-to-paid conversion dropped below industry benchmark',
] as const;

export const VALUE_HOOKS = [
  'Cut research time per lead from 40 minutes to under 3',
  'Personalize 1,000 outbound emails with one enriched brief',
  'See exactly which sequence step drove each reply',
  'Roll out a new ICP and have enriched leads the same day',
] as const;

export const PROOFS = [
  'Helped a Series B SaaS 3x reply rates in 6 weeks',
  'Trusted by 200+ B2B sales teams across Europe',
  'G2 leader in sales intelligence, two quarters running',
] as const;

export const SUBJECT_LINES = [
  'Quick idea for {{company}}\'s outbound',
  '{{firstName}} — saw your team is hiring SDRs',
  'The 40-min problem in your sales pipeline',
  '{{firstName}}, a fit for {{company}}?',
  'How {{company}} could 3x reply rates',
] as const;

export const OUTREACH_BODIES = [
  'Hi {{firstName}}, noticed {{company}} just expanded into DACH — most teams we work with ({{proof}}) hit the same research bottleneck at this stage. Worth a quick look?',
  'Hi {{firstName}}, the pattern we see at fast-growing {{industry}} companies is research eating 40% of SDR time. We got {{company}} peers under 3 min/lead. Open to a 15-min walkthrough?',
  'Hi {{firstName}}, last note from me — {{company}} feels like a strong fit given {{trigger}}. If pipeline velocity is on the Q3 plan, I\'d love to show you how this works.',
] as const;

export const REPLIES = [
  'Interesting — can you send a one-pager?',
  'What does pricing look like for a team of 10?',
  'Not the right time, but keep me posted.',
  'Can we move this to next week? Busy now.',
  'This looks relevant. Looping in our Head of Growth.',
  'Already evaluating a similar tool, thanks.',
] as const;

export const SUGGESTIONS = [
  'Happy to send a one-pager — here it is. Want me to also share a 5-min demo?',
  'Teams of 10 start at €490/mo. Want me to set up a sandbox so you can try it before we talk?',
  'No problem — I\'ll follow up next quarter. Is there a better time to reconnect?',
] as const;

// ---------------------------------------------------------------------------
// Campaign naming
// ---------------------------------------------------------------------------

export const CAMPAIGN_THEMES = [
  'DACH Q3 Outbound', 'Nordic SaaS Launch', 'Series B Expansion', 'Enterprise Push',
  'RevOps Reset', 'Cybersecurity Sweep', 'Climate Tech Roundup', 'Fintech Leaders',
  'HealthTech Connect', 'DevTools Builder Tour', 'Mid-Market Martech', 'EU Growth Sprint',
] as const;

export const CAMPAIGN_LOCATIONS = [
  'DACH Region (DE/AT/CH)', 'Nordics (SE/NO/DK/FI)', 'Western Europe (FR/NL/BE)',
  'North America (US/CA)', 'Iberia (ES/PT)', 'UK & Ireland',
] as const;

export const CAMPAIGN_OBJECTIVES = [
  'Book qualified demos with VP-level buyers',
  'Generate product-qualified leads through a free audit',
  'Fill the sales pipeline for Q4 with ICP-fit accounts',
  'Drive trial sign-ups from high-intent accounts',
] as const;

export const CAMPAIGN_CHANNELS = ['email', 'linkedin', 'whatsapp', 'phone'] as const;

export const CAMPAIGN_STATUS = ['active', 'paused', 'archived', 'completed'] as const;

/** Template helper — fill {{tokens}} in a copy string. */
export const fill = (
  template: string,
  vars: Record<string, string>,
): string => template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? '');
